import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-600 mb-4">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-label whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-4-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 5);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 漫画全集在线免费阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.chapters[comic.chapters.length - 1]?.updatedAt,
    dateModified: comic.updatedAt,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://36manhua.com' },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${category?.name || ''}漫画`,
        item: `https://36manhua.com/category/${comic.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: comic.title,
        item: `https://36manhua.com/comic/${comic.id}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 漫画全集在线免费阅读`}
        description={comic.description}
        keywords={`${comic.title},${comic.tags.join(',')},漫画全集,在线免费阅读,36漫画网`}
        canonicalUrl={`https://36manhua.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: `${category?.name || ''}漫画`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <article className="py-6">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0 w-full sm:w-[240px] md:w-[280px] mx-auto md:mx-0">
                  <div className="rounded-lg overflow-hidden aspect-[5/7]">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.author}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                    {comic.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                    <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded whitespace-nowrap">
                      {comic.status}
                    </span>
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-foreground-600 bg-secondary-100 px-2 py-0.5 rounded whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="text-foreground-900 font-medium">{comic.rating}</span>
                      <span className="text-foreground-500">评分</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-eye-line"></i>
                      <span>{(comic.views / 10000).toFixed(1)}万 阅读</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-heart-line"></i>
                      <span>{(comic.favorites / 10000).toFixed(1)}万 收藏</span>
                    </div>
                    <div className="flex items-center gap-1 text-foreground-500">
                      <i className="ri-calendar-line"></i>
                      <span>更新至 {comic.updatedAt}</span>
                    </div>
                  </div>

                  <div className="bg-secondary-50 rounded-lg p-4 mb-5">
                    <h2 className="text-base font-heading text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">{comic.fullDescription}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/comic/${comic.id}`}
                      className="flex-1 text-center px-6 py-3 bg-primary-500 text-background-50 rounded-full text-base font-label whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-book-open-line mr-2"></i>开始阅读第一话
                    </Link>
                    <button
                      onClick={() => {
                        const el = document.getElementById('comic-chapters');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex-1 px-6 py-3 bg-secondary-200 text-foreground-800 rounded-full text-base font-label whitespace-nowrap cursor-pointer hover:bg-secondary-300 transition-colors"
                    >
                      <i className="ri-list-check mr-2"></i>查看章节列表
                    </button>
                  </div>
                </div>
              </div>

              <div id="comic-chapters" className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-heading text-foreground-900">
                    章节列表
                    <span className="text-sm text-foreground-500 font-body ml-2">
                      共 {comic.chapters.length} 话
                    </span>
                  </h2>
                </div>
                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  {displayedChapters.map((ch, index) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}`}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-secondary-50 transition-colors ${
                        index < displayedChapters.length - 1 ? 'border-b border-background-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-label text-foreground-900 whitespace-nowrap">
                          第{ch.number}话
                        </span>
                        <span className="text-sm text-foreground-700 truncate">{ch.title}</span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">{ch.updatedAt}</span>
                    </Link>
                  ))}
                </div>
                {comic.chapters.length > 5 && (
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setShowAllChapters(!showAllChapters)}
                      className="px-6 py-2 bg-secondary-100 text-foreground-700 rounded-full text-sm font-label whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
                    >
                      {showAllChapters ? (
                        <>
                          <i className="ri-arrow-up-s-line mr-1"></i>收起
                        </>
                      ) : (
                        <>
                          查看全部 {comic.chapters.length} 话 <i className="ri-arrow-down-s-line ml-1"></i>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-10">
                <h2 className="text-xl font-heading text-foreground-900 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                  {comics
                    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
                    .slice(0, 5)
                    .map((related) => (
                      <Link
                        key={related.id}
                        to={`/comic/${related.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="rounded-lg overflow-hidden mb-2 aspect-[5/7]">
                          <img
                            src={related.coverImage}
                            alt={related.title}
                            title={`${related.title} - ${related.author}`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <h4 className="text-xs font-heading text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {related.title}
                        </h4>
                      </Link>
                    ))}
                </div>
              </div>
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}